import React from 'react'
import {
  ButtonVariant,
  MaxWidthConstraint,
  OneByFourGrid,
  OvalButton,
  PhotoHero,
  Spacer,
  WorkArea,
} from '@revelrygroup/components'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import Image from 'next/image'

import { API } from '../revelry_api'
import { FuneralHome } from '../types'
import { FuneralHomeFooter } from '../components/FuneralHomeFooter'
import { FuneralHomeModel } from '../components/models/FuneralHomeModel'
import Layout from '../components/Layout'
import { routes } from '../routes'
import * as Showcase from '../components/Showcase'
import styles from '../styles/HowWeCelebrate.module.scss'
import { Testimonials } from '../components/Testimonials'

interface Props {
  funeralHome: FuneralHome | null
}

const TheKalasDifference: React.FC<Props> = ({ funeralHome }) => {
  return (
    <>
      <Head>
        <title>
          The Kalas Difference - {funeralHome.name} -{' '}
          {FuneralHomeModel.headTagLocation(funeralHome)}
        </title>

        <meta
          name="description"
          content={`${funeralHome.name} is the only funeral home in the Annapolis area with an on-site crematory. From our non-denominational chapel and reception rooms to personalized tributes and professional catering, we help families gather to celebrate a loved one's life.`}
          key="description"
        />
      </Head>

      <Layout>
        <PhotoHero>The Kalas Difference</PhotoHero>

        <div className="height-100">
          <main>
            <WorkArea className="blue-gradient-background">
              <MaxWidthConstraint>
                <Spacer verticalBottom="sm">
                  <h2 className="h2 centered-text">
                    We&apos;re the Only Funeral Home in the Annapolis Area With An On-Site Crematory
                  </h2>
                </Spacer>

                <div className={styles.split}>
                  <div className={styles.splitText}>
                    <Spacer verticalBottom="xs">
                      <p className="body centered-text">
                        Many funeral homes promise your loved one never leaves their{' '}
                        <strong>care</strong>. At Kalas, your loved one never leaves our{' '}
                        <strong>home.</strong>
                      </p>
                    </Spacer>

                    <Spacer verticalBottom="xs">
                      <p className="body centered-text">
                        Elsewhere, cremation often takes place at an off-site facility shared by
                        multiple funeral homes. We&apos;re different.
                      </p>
                    </Spacer>

                    <Spacer verticalBottom="xs">
                      <p className="body centered-text">
                        Whether you choose our Edgewater or National Harbor location, your loved one
                        is cared for by the dedicated Kalas team throughout the entire process,
                        including our private on-site crematory.
                      </p>
                    </Spacer>

                    <p className="body centered-text">
                      No transfers. No third parties. Just the certainty of knowing your loved one
                      never leaves our home.
                    </p>
                  </div>

                  <div className={styles.splitImage}>
                    <Image
                      alt="Annapolis harbor and the Maryland State House"
                      height={935}
                      layout="responsive"
                      src="/photo-annapolis-harbor@2x.jpg"
                      width={1400}
                    />
                  </div>
                </div>
              </MaxWidthConstraint>
            </WorkArea>

            <Showcase.Wrapper backgroundImage="/radian.png" contentClassName={styles.eljContent}>
              <Showcase.Secondary>
                <Showcase.ImageContainer>
                  <Image
                    alt="A family walking together on the beach"
                    height={948}
                    layout="responsive"
                    src="/photo-family-beach-walk@2x.jpg"
                    unoptimized
                    width={1400}
                  />
                </Showcase.ImageContainer>
              </Showcase.Secondary>

              <Showcase.Primary>
                <Showcase.Title className="centered-text-mobile">
                  Every Life&apos;s Journey Deserves to Be Celebrated
                </Showcase.Title>

                <Showcase.Text>
                  Every life is unique, and every tribute should be, too.
                </Showcase.Text>

                <Showcase.Text>
                  We&apos;ll help you create a meaningful celebration that reflects your loved
                  one&apos;s journey, honors their legacy, and brings family and friends together
                  through the stories and memories that matter most.
                </Showcase.Text>
              </Showcase.Primary>
            </Showcase.Wrapper>

            <WorkArea style={{ backgroundColor: 'white' }}>
              <MaxWidthConstraint className="centered-text">
                <Spacer verticalBottom="xs">
                  <h2 className="h2">Where Families Gather and Memories Live On</h2>
                </Spacer>

                <Spacer verticalBottom="xs">
                  <p className="body">
                    Whether you envision an intimate gathering, a traditional service, or a
                    reception where family and friends can share stories and laughter, we&apos;re
                    here to help you bring everyone together in a warm and welcoming setting.
                  </p>
                </Spacer>

                <Spacer verticalBottom="xs">
                  <p className="body">
                    From our non-denominational chapel and spacious reception rooms to personalized
                    tributes and professional catering, every detail is thoughtfully designed to
                    honor your loved one&apos;s life and create lasting memories for those who
                    gather to celebrate it.
                  </p>
                </Spacer>

                <Spacer verticalTop="sm">
                  <OvalButton as="Link" href={routes.contact} variant={ButtonVariant.DEFAULT_DARK}>
                    Contact Us
                  </OvalButton>
                </Spacer>

                <Spacer verticalTop="md">
                  <OneByFourGrid>
                    <div>
                      <Image
                        alt="Spacious gathering rooms"
                        height={554}
                        layout="responsive"
                        src="/photo-spacious-gathering@2x.jpg"
                        width={554}
                      />
                      <p className={styles.caption}>
                        Spacious
                        <br />
                        Gathering Rooms
                      </p>
                    </div>

                    <div>
                      <Image
                        alt="Chapel seating for services"
                        height={554}
                        layout="responsive"
                        src="/photo-chapels@2x.jpg"
                        width={554}
                      />
                      <p className={styles.caption}>
                        Chapels Seat
                        <br />
                        100 &amp; 150 Guests
                      </p>
                    </div>

                    <div>
                      <Image
                        alt="Personalized celebration centerpiece"
                        height={554}
                        layout="responsive"
                        src="/photo-personalized-celebrations@2x.jpg"
                        width={554}
                      />
                      <p className={styles.caption}>
                        Personalized
                        <br />
                        Celebrations
                      </p>
                    </div>

                    <div>
                      <Image
                        alt="Professional catering spread"
                        height={554}
                        layout="responsive"
                        src="/photo-professional-catering@2x.jpg"
                        width={554}
                      />
                      <p className={styles.caption}>
                        Professional
                        <br />
                        Catering
                      </p>
                    </div>
                  </OneByFourGrid>
                </Spacer>
              </MaxWidthConstraint>
            </WorkArea>

            <Testimonials />
          </main>

          <FuneralHomeFooter />
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  try {
    const funeralHome = await API.funeralHomes.load()

    return { props: { funeralHome } }
  } catch (e) {
    return { props: { funeralHome: null } }
  }
}

export default TheKalasDifference
